import React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import StatusChipFinanceiro from './StatusChipFinanceiro';
import { formatCurrency } from '../../utils/money';
import type { ExtratoLinha } from '../../types/financeiro';

// Puramente apresentacional (ai/architecture.core.md §5, "Components MUST NOT fetch data") —
// nenhum import de service, nenhum hook de leitura do Apollo, nenhum fetch de dados. Recebe
// linhas/paginação/callbacks por props; quem busca dados e quem navega é a page (ExtratoTab).

export interface TabelaExtratoProps {
  linhas: ExtratoLinha[];
  page: number;
  size: number;
  totalItems: number;
  loading: boolean;
  error: Error | undefined;
  onPageChange: (page: number) => void;
  onSizeChange: (size: number) => void;
  onVerRecibo: (linha: ExtratoLinha) => void;
  onVerDetalhe: (linha: ExtratoLinha) => void;
}

const TITULO_DATA: Record<ExtratoLinha['tipo'], string> = {
  INSCRICAO: 'Recorte por data de competência do pagamento',
  AGENDAMENTO: 'Data do pedido de agendamento',
  REEMBOLSO: 'Data do pedido de reembolso',
};

const ROTULO_TIPO: Record<ExtratoLinha['tipo'], string> = {
  INSCRICAO: 'Inscrição',
  AGENDAMENTO: 'Agendamento',
  REEMBOLSO: 'Reembolso',
};

const TabelaExtrato: React.FC<TabelaExtratoProps> = ({
  linhas,
  page,
  size,
  totalItems,
  loading,
  error,
  onPageChange,
  onSizeChange,
  onVerRecibo,
  onVerDetalhe,
}) => {
  if (error) {
    return null;
  }

  if (loading) {
    return <div className="text-center py-4">Carregando extrato...</div>;
  }

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Data</TableCell>
            <TableCell>Tipo</TableCell>
            <TableCell>Descrição</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Valor</TableCell>
            <TableCell>Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {linhas.map((linha) => (
            <TableRow key={linha.referenciaId}>
              <TableCell title={TITULO_DATA[linha.tipo]}>{linha.data}</TableCell>
              <TableCell>{ROTULO_TIPO[linha.tipo]}</TableCell>
              <TableCell>{linha.descricao ?? '—'}</TableCell>
              <TableCell>
                <StatusChipFinanceiro status={linha.status} />
              </TableCell>
              <TableCell>{formatCurrency(linha.valor)}</TableCell>
              <TableCell>
                {linha.reciboUrl && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 me-3"
                    onClick={() => onVerRecibo(linha)}
                  >
                    Ver recibo
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0"
                  onClick={() => onVerDetalhe(linha)}
                >
                  Ver detalhe
                </button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={totalItems}
        page={page}
        onPageChange={(_event, newPage) => onPageChange(newPage)}
        rowsPerPage={size}
        onRowsPerPageChange={(event) => onSizeChange(parseInt(event.target.value, 10))}
        rowsPerPageOptions={[10, 20, 50]}
        labelRowsPerPage="Itens por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`}
      />
    </TableContainer>
  );
};

export default TabelaExtrato;
